import { StyleSheet } from 'react-native';

export const createStyles = (theme: 'light' | 'dark' = 'light', gap?: number) => {
  const colors = {
    light: {
      background: '#FFFFFF',
      text: '#333333',
      subText: '#999999',
      priceColor: '#FF2442',
      originalPrice: '#CCCCCC',
      border: '#F0F0F0',
      tagBackground: '#FFF5F5',
      tagText: '#FF2442',
      tagBorder: '#FF2442',
      buyButton: '#FF2442',
      buyButtonText: '#FFFFFF',
      favoriteActive: '#FF2442',
      favoriteInactive: '#CCCCCC',
      outOfStock: 'rgba(0,0,0,0.5)',
      badgeBackground: '#FF2442',
      badgeText: '#FFFFFF',
      ratingColor: '#FFB800',
      // 购物车模式新增
      checkboxBorder: '#CCCCCC',
      checkboxActive: '#FF2442',
      checkboxBg: '#FFFFFF',
      skuText: '#666666',
      skuHint: '#999999',
      cartTagBg: '#FFF5F5',
      cartTagText: '#FF2442',
      cartTagBorder: '#FF2442',
      deleteIcon: '#999999',
    },
    dark: {
      background: '#2A2A2A',
      text: '#FFFFFF',
      subText: '#999999',
      priceColor: '#FF5566',
      originalPrice: '#666666',
      border: '#3A3A3A',
      tagBackground: '#3A2020',
      tagText: '#FF5566',
      tagBorder: '#FF5566',
      buyButton: '#FF5566',
      buyButtonText: '#FFFFFF',
      favoriteActive: '#FF5566',
      favoriteInactive: '#666666',
      outOfStock: 'rgba(0,0,0,0.7)',
      badgeBackground: '#FF5566',
      badgeText: '#FFFFFF',
      ratingColor: '#FFB800',
      // 购物车模式新增
      checkboxBorder: '#666666',
      checkboxActive: '#FF5566',
      checkboxBg: '#2A2A2A',
      skuText: '#AAAAAA',
      skuHint: '#888888',
      cartTagBg: '#3A2020',
      cartTagText: '#FF5566',
      cartTagBorder: '#FF5566',
      deleteIcon: '#888888',
    },
  };

  const c = colors[theme];
  const defaultGap = gap ?? 8;

  return StyleSheet.create({
    // 垂直卡片 (默认)
    verticalContainer: {
      backgroundColor: c.background,
      borderRadius: 8,
      overflow: 'hidden',
    },
    verticalImage: {
      width: '100%',
      aspectRatio: 1,
      resizeMode: 'cover',
    },
    verticalContent: {
      padding: 8,
    },
    // 水平卡片
    horizontalContainer: {
      flexDirection: 'row',
      backgroundColor: c.background,
      borderRadius: 8,
      overflow: 'hidden',
      padding: 12,
    },
    horizontalImage: {
      width: 100,
      height: 100,
      borderRadius: 6,
      resizeMode: 'cover',
    },
    horizontalContent: {
      flex: 1,
      marginLeft: 12,
      justifyContent: 'space-between',
    },
    // Grid卡片
    gridContainer: {
      backgroundColor: c.background,
      borderRadius: 8,
      overflow: 'hidden',
    },
    gridImage: {
      width: '100%',
      aspectRatio: 1,
      resizeMode: 'cover',
    },
    gridContent: {
      padding: 8,
    },
    // 瀑布流卡片（双列布局）
    waterfallContainer: {
      backgroundColor: c.background,
      borderRadius: 12,
      overflow: 'hidden',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.06,
      shadowRadius: 8,
      elevation: 3,
    },
    waterfallImage: {
      width: '100%',
      aspectRatio: 0.85, // 高度为宽度的85%，非正方形
      resizeMode: 'cover',
    },
    waterfallContent: {
      padding: 10,
      paddingBottom: 12,
    },
    // List卡片
    listContainer: {
      flexDirection: 'row',
      backgroundColor: c.background,
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.border,
    },
    listImage: {
      width: 80,
      height: 80,
      borderRadius: 4,
      resizeMode: 'cover',
    },
    listContent: {
      flex: 1,
      marginLeft: 12,
    },
    // ===== 购物车模式新增样式 =====
    cartContainer: {
      flexDirection: 'row',
      backgroundColor: c.background,
      paddingVertical: 12,
      paddingHorizontal: 12,
      alignItems: 'flex-start', // 顶部对齐，让图片和标题顶部对齐
    },
    // 选择框区域 - 与图片顶部对齐
    checkboxContainer: {
      justifyContent: 'flex-start',
      alignItems: 'center',
      marginRight: 10,
      paddingTop: 35, // 垂直居中于图片 (88/2 - 18/2 ≈ 35)
      height: 88, // 与图片高度一致
    },
    checkbox: {
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 1.5,
      borderColor: c.checkboxBorder,
      backgroundColor: c.checkboxBg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxActive: {
      borderColor: c.checkboxActive,
      backgroundColor: c.checkboxActive,
    },
    checkboxInner: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: '#FFFFFF',
    },
    checkboxIcon: {
      color: '#FFFFFF',
      fontSize: 12,
    },
    // 图片区域
    cartImageSection: {
      position: 'relative',
      alignItems:'flex-start'
    },
    cartImage: {
      width: 88,
      height: 88,
      borderRadius: 8,
      resizeMode: 'cover',
    },
    // 内容区域
    cartContent: {
      flex: 1,
      marginLeft: 10,
      justifyContent: 'flex-start',
    },
    cartTopSection: {
      // 不设置 flex: 1，让内容自然排列
    },
    // 购物车标签
    cartTagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 6,
    },
    cartTag: {
      paddingHorizontal: 4,
      paddingVertical: 2,
      borderRadius: 2,
      backgroundColor: c.cartTagBg,
      borderWidth: 0.5,
      borderColor: c.cartTagBorder,
    },
    cartTagText: {
      fontSize: 10,
      color: c.cartTagText,
    },
    // SKU 选择器
    skuContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 2,
    },
    skuText: {
      fontSize: 12,
      color: c.skuText,
    },
    skuArrow: {
      marginLeft: 4,
    },
    // 价格和数量行
    cartBottomSection: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 'auto', // 将价格区域推到内容区域底部
    },
    // 删除按钮
    deleteButton: {
      padding: 4,
      marginLeft: 8,
    },
    // 底部扩展区域
    cartBottomExtra: {
      paddingTop: defaultGap,
      // borderTopWidth: StyleSheet.hairlineWidth,
      // borderTopColor: c.border,
      marginTop: defaultGap,
    },
    // 数量文本（点击显示输入框）
    quantityTextContainer: {
      paddingHorizontal: 8,
      paddingVertical: 4,
      backgroundColor: '#f5f5f5',
      borderRadius: 4,
    },
    quantityText: {
      fontSize: 13,
      color: '#666666',
    },
    // 间距工具类
    gapXs: { marginTop: 4 },
    gapSm: { marginTop: 6 },
    gapMd: { marginTop: defaultGap },
    gapLg: { marginTop: 12 },
    // 通用样式
    title: {
      fontSize: 14,
      fontWeight: '500',
      color: c.text,
      lineHeight: 20,
    },
    titleTwoLines: {
      height: 40,
    },
    subtitle: {
      fontSize: 12,
      color: c.subText,
      lineHeight: 16,
    },
    // 价格
    priceContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    pricePrefix: {
      fontSize: 12,
      color: c.priceColor,
      fontWeight: '600',
    },
    priceValue: {
      fontSize: 16,
      color: c.priceColor,
      fontWeight: '700',
    },
    priceSuffix: {
      fontSize: 12,
      color: c.priceColor,
    },
    originalPrice: {
      fontSize: 11,
      color: c.originalPrice,
      textDecorationLine: 'line-through',
      marginLeft: 4,
    },
    // 标签
    tagsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 4,
    },
    tag: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 2,
      backgroundColor: c.tagBackground,
    },
    tagOutline: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: c.tagBorder,
    },
    tagText: {
      fontSize: 10,
      color: c.tagText,
    },
    // 销量/评分
    infoContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    salesText: {
      fontSize: 11,
      color: c.subText,
    },
    ratingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    ratingStar: {
      fontSize: 11,
      color: c.ratingColor,
    },
    ratingText: {
      fontSize: 11,
      color: c.subText,
      marginLeft: 2,
    },
    reviewCount: {
      fontSize: 11,
      color: c.subText,
      marginLeft: 4,
    },
    shopName: {
      fontSize: 11,
      color: c.subText,
    },
    // 按钮区域
    buttonsContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    buyButton: {
      paddingHorizontal: 12,
      paddingVertical: 6,
      backgroundColor: c.buyButton,
      borderRadius: 14,
    },
    buyButtonText: {
      fontSize: 12,
      color: c.buyButtonText,
      fontWeight: '500',
    },
    favoriteButton: {
      padding: 4,
    },
    favoriteIcon: {
      fontSize: 20,
    },
    favoriteActive: {
      color: c.favoriteActive,
    },
    favoriteInactive: {
      color: c.favoriteInactive,
    },
    // 角标
    badge: {
      position: 'absolute',
      top: 0,
      left: 0,
      paddingHorizontal: 6,
      paddingVertical: 2,
      backgroundColor: c.badgeBackground,
      borderBottomRightRadius: 8,
    },
    badgeText: {
      fontSize: 10,
      color: c.badgeText,
      fontWeight: '600',
    },
    // 缺货遮罩
    outOfStockOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.outOfStock,
      alignItems: 'center',
      justifyContent: 'center',
    },
    outOfStockText: {
      fontSize: 14,
      color: '#FFFFFF',
      fontWeight: '600',
    },
    // 右侧区域
    rightExtraContainer: {
      marginLeft: 8,
      justifyContent: 'center',
    },
    // Footer
    footerContainer: {
      marginTop: 8,
    },
  });
};

export default createStyles;
